/**
 * Product Gallery Modal - Shared JavaScript for Product Page and Quick View
 * Handles modal controls: open, close, navigation, zoom in/out, variant label hiding
 */

(function() {
  'use strict';
  
  // Zoom state - per modal
  const zoomStates = new Map();
  
  const minZoom = 1;
  const maxZoom = 5;
  const zoomStep = 0.25;
  
  function getZoomState(modal) {
    if (!zoomStates.has(modal)) {
      zoomStates.set(modal, { currentZoom: 1 });
    }
    return zoomStates.get(modal);
  }
  
  function getActiveModalImage(modal) {
    if (!modal) return null;
    const activeSlide = modal.querySelector('.product-gallery__modal-slide--active');
    return activeSlide ? activeSlide.querySelector('.product-gallery__modal-image') : null;
  }
  
  function applyZoom(modal, zoomLevel) {
    const img = getActiveModalImage(modal);
    if (!img) return;
    
    const state = getZoomState(modal);
    state.currentZoom = Math.max(minZoom, Math.min(maxZoom, zoomLevel));
    
    if (state.currentZoom === 1) {
      img.style.transform = 'scale(1)';
      img.style.transformOrigin = 'center center';
      img.classList.remove('zoomed');
    } else {
      img.style.transform = `scale(${state.currentZoom})`;
      img.style.transformOrigin = 'center center';
      img.classList.add('zoomed');
    }
  }
  
  function zoomIn(modal) {
    const state = getZoomState(modal);
    applyZoom(modal, state.currentZoom + zoomStep);
  }
  
  function zoomOut(modal) {
    const state = getZoomState(modal);
    applyZoom(modal, state.currentZoom - zoomStep);
  }
  
  function resetZoom(modal) {
    const state = getZoomState(modal);
    state.currentZoom = 1;
    const img = getActiveModalImage(modal);
    if (img) {
      img.style.transform = 'scale(1)';
      img.style.transformOrigin = 'center center';
      img.classList.remove('zoomed');
    }
  }
  
  /**
   * Hide variant labels when gallery modal opens
   * Works for both product page and quick view
   */
  function hideVariantLabels(container) {
    const selectors = [
      'label.product-form__option-value',
      'label[for*="Option-"]',
      '.product-form__option-value',
      '[class*="Option-template"]'
    ];
    
    let allFound = [];
    selectors.forEach(selector => {
      const found = container.querySelectorAll(selector);
      if (found.length > 0) {
        allFound.push(...Array.from(found));
      }
    });
    
    // Remove duplicates
    const uniqueLabels = Array.from(new Set(allFound));
    
    if (uniqueLabels.length > 0) {
      uniqueLabels.forEach(label => {
        // Use multiple hiding methods to ensure they're hidden
        label.style.setProperty('display', 'none', 'important');
        label.style.setProperty('visibility', 'hidden', 'important');
        label.style.setProperty('opacity', '0', 'important');
        label.style.setProperty('pointer-events', 'none', 'important');
        label.style.setProperty('z-index', '-9999', 'important');
        label.style.setProperty('position', 'absolute', 'important');
        label.style.setProperty('left', '-9999px', 'important');
        label.style.setProperty('width', '0', 'important');
        label.style.setProperty('height', '0', 'important');
        label.style.setProperty('overflow', 'hidden', 'important');
      });
    }
  }
  
  /**
   * Handle modal becoming active - reset zoom and hide variant labels
   */
  function handleModalActive(modal) {
    resetZoom(modal);
    
    // Hide variant labels in the modal itself first
    hideVariantLabels(modal);
    
    // If modal is inside quick view, also handle quick view specific things
    const quickViewModal = modal.closest('.quick-view-modal');
    if (quickViewModal) {
      quickViewModal.classList.add('gallery-modal-active');
      // Hide variant labels in quick view container
      hideVariantLabels(quickViewModal);
      // Also search in the body of quick view
      const quickViewBody = quickViewModal.querySelector('.quick-view-modal__body');
      if (quickViewBody) {
        hideVariantLabels(quickViewBody);
      }
    } else {
      // Product page - hide variant labels in the section
      const productSection = modal.closest('[data-section-type="product"], .product-page');
      if (productSection) {
        hideVariantLabels(productSection);
      }
    }
    
    // Also do a document-wide search for any variant labels that might be positioned absolutely over the modal
    setTimeout(() => {
      const allVariantLabels = document.querySelectorAll('label.product-form__option-value, label[for*="Option-"]');
      allVariantLabels.forEach(label => {
        const rect = label.getBoundingClientRect();
        const modalRect = modal.getBoundingClientRect();
        // Check if label is visually overlapping with modal
        if (rect.top < modalRect.bottom && rect.bottom > modalRect.top &&
            rect.left < modalRect.right && rect.right > modalRect.left) {
          label.style.setProperty('display', 'none', 'important');
          label.style.setProperty('visibility', 'hidden', 'important');
          label.style.setProperty('opacity', '0', 'important');
          label.style.setProperty('z-index', '-9999', 'important');
        }
      });
    }, 50);
  }
  
  /**
   * Handle modal becoming inactive
   */
  function handleModalInactive(modal) {
    const quickViewModal = modal.closest('.quick-view-modal');
    if (quickViewModal) {
      quickViewModal.classList.remove('gallery-modal-active');
    }
  }
  
  /**
   * Close modal handler
   */
  function closeModal(modal) {
    if (!modal) return;
    
    modal.classList.remove('product-gallery__modal--active');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    
    // Show header/navbar again
    const header = document.querySelector('[data-header]') || document.querySelector('.header, header');
    if (header) header.style.display = '';
    
    // Show quick view close button if it exists
    const quickViewClose = document.querySelector('.quick-view-modal__close');
    if (quickViewClose) quickViewClose.style.display = '';
    
    handleModalInactive(modal);
    resetZoom(modal);
  }
  
  /**
   * Navigate to next slide
   */
  function navigateNext(modal) {
    const slides = modal.querySelectorAll('.product-gallery__modal-slide');
    const activeIndex = Array.from(slides).findIndex(s => s.classList.contains('product-gallery__modal-slide--active'));
    const nextIndex = (activeIndex + 1) % slides.length;
    
    slides.forEach((slide, index) => {
      slide.classList.toggle('product-gallery__modal-slide--active', index === nextIndex);
    });
    
    resetZoom(modal);
  }
  
  /**
   * Navigate to previous slide
   */
  function navigatePrev(modal) {
    const slides = modal.querySelectorAll('.product-gallery__modal-slide');
    const activeIndex = Array.from(slides).findIndex(s => s.classList.contains('product-gallery__modal-slide--active'));
    const prevIndex = (activeIndex - 1 + slides.length) % slides.length;
    
    slides.forEach((slide, index) => {
      slide.classList.toggle('product-gallery__modal-slide--active', index === prevIndex);
    });
    
    resetZoom(modal);
  }
  
  /**
   * Initialize modal event handlers
   */
  function initModalHandlers() {
    // Use event delegation on document with capture phase
    document.addEventListener('click', function(e) {
      // Ensure we have an Element node (not text node)
      const target = e.target.nodeType === Node.ELEMENT_NODE ? e.target : e.target.parentElement;
      if (!target) {
        return;
      }

      // Open modal from in-gallery zoom button (works for product page and quick view)
      const zoomTrigger = target.closest('.product-gallery__zoom');
      if (zoomTrigger) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const gallery = zoomTrigger.closest('.product-gallery') || zoomTrigger.closest('.product-quick-view') || zoomTrigger.closest('.product-page');
        const modal = (gallery && gallery.querySelector('.product-gallery__modal')) ||
          zoomTrigger.closest('.quick-view-modal')?.querySelector('.product-gallery__modal') ||
          document.querySelector('.product-gallery__modal');

        if (!modal) return false;

        // Determine current image index (prefer explicit data-image-index)
        const imageWrapper = zoomTrigger.closest('.product-gallery__image-wrapper');
        let index = null;
        if (imageWrapper) {
          const indexStr = imageWrapper.getAttribute('data-image-index');
          if (indexStr !== null) {
            const parsed = parseInt(indexStr, 10);
            if (!Number.isNaN(parsed)) index = parsed;
          }
        }

        // Fallback: use active slide index
        if (index === null && gallery) {
          const slides = gallery.querySelectorAll('.product-gallery__slide');
          const activeIdx = Array.from(slides).findIndex(s => s.classList.contains('product-gallery__slide--active'));
          if (activeIdx >= 0) index = activeIdx;
        }

        // Activate correct slide
        if (index !== null) {
          const modalSlides = modal.querySelectorAll('.product-gallery__modal-slide');
          modalSlides.forEach((slide, i) => {
            slide.classList.toggle('product-gallery__modal-slide--active', i === index);
          });
        }

        // Hide quick view close button if present (it can overlap the gallery modal)
        const quickViewClose = document.querySelector('.quick-view-modal__close');
        if (quickViewClose) quickViewClose.style.display = 'none';

        // Show modal
        modal.classList.add('product-gallery__modal--active');
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        // Ensure zoom state + label hiding happen even if observer hasn't attached yet
        handleModalActive(modal);

        return false;
      }
      
      // Check for close button
      const closeButton = target.closest('.product-gallery__modal-close');
      if (closeButton) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const modal = target.closest('.product-gallery__modal');
        if (modal && modal.classList.contains('product-gallery__modal--active')) {
          closeModal(modal);
        }
        return false;
      }
      
      // Check for overlay click
      if (target.classList.contains('product-gallery__modal-overlay')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const modal = target.closest('.product-gallery__modal');
        if (modal && modal.classList.contains('product-gallery__modal--active')) {
          closeModal(modal);
        }
        return false;
      }
      
      // Check for zoom in button
      const zoomInBtn = target.closest('.product-gallery__modal-zoom-in');
      if (zoomInBtn) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const modal = target.closest('.product-gallery__modal');
        if (modal && modal.classList.contains('product-gallery__modal--active')) {
          zoomIn(modal);
        }
        return false;
      }
      
      // Check for zoom out button
      const zoomOutBtn = target.closest('.product-gallery__modal-zoom-out');
      if (zoomOutBtn) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const modal = target.closest('.product-gallery__modal');
        if (modal && modal.classList.contains('product-gallery__modal--active')) {
          zoomOut(modal);
        }
        return false;
      }
      
      // Check for next button
      const nextBtn = target.closest('.product-gallery__modal-next');
      if (nextBtn) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const modal = target.closest('.product-gallery__modal');
        if (modal && modal.classList.contains('product-gallery__modal--active')) {
          navigateNext(modal);
        }
        return false;
      }
      
      // Check for prev button
      const prevBtn = target.closest('.product-gallery__modal-prev');
      if (prevBtn) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const modal = target.closest('.product-gallery__modal');
        if (modal && modal.classList.contains('product-gallery__modal--active')) {
          navigatePrev(modal);
        }
        return false;
      }
    }, true); // Capture phase
  }
  
  /**
   * Initialize mutation observer to watch for modal state changes
   */
  function initModalObserver() {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const modal = mutation.target;
          
          if (modal.classList.contains('product-gallery__modal--active')) {
            const oldValue = mutation.oldValue || '';
            if (!oldValue.includes('product-gallery__modal--active')) {
              // Modal just became active
              handleModalActive(modal);
            }
          } else {
            const oldValue = mutation.oldValue || '';
            if (oldValue.includes('product-gallery__modal--active')) {
              // Modal just became inactive
              handleModalInactive(modal);
            }
          }
        }
      });
    });
    
    // Observe all existing modals
    document.querySelectorAll('.product-gallery__modal').forEach(function(modal) {
      observer.observe(modal, { attributes: true, attributeOldValue: true });
    });
    
    // Also observe for dynamically added modals (quick view)
    const bodyObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            const modals = node.querySelectorAll ? node.querySelectorAll('.product-gallery__modal') : [];
            modals.forEach(function(modal) {
              observer.observe(modal, { attributes: true, attributeOldValue: true });
            });
            if (node.classList && node.classList.contains('product-gallery__modal')) {
              observer.observe(node, { attributes: true, attributeOldValue: true });
            }
          }
        });
      });
    });
    
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }
  
  /**
   * ESC key to close
   */
  function initKeyboardHandlers() {
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        const activeModal = document.querySelector('.product-gallery__modal--active');
        if (activeModal) {
          closeModal(activeModal);
        }
      }
    });
  }
  
  /**
   * Initialize everything
   */
  function init() {
    initModalHandlers();
    initModalObserver();
    initKeyboardHandlers();
  }
  
  // Initialize immediately
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Also listen for quick view content loaded (custom event)
  document.addEventListener('quickViewContentLoaded', function() {
    // Re-observe for new modals
    initModalObserver();
  });
  
})();


